import { localApiClient } from "./localApiClient";

const norm = (value) => String(value || "").trim();
const lower = (value) => norm(value).toLowerCase();

const toKobo = (value) => Math.round((Number(value) || 0) * 100);
const fromKobo = (value) => Number(((Number(value) || 0) / 100).toFixed(2));

const serviceCodeFromName = (name) =>
  lower(name)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || `SERVICE-${Date.now()}`;

export const fromLocalService = (service = {}) => {
  const price = fromKobo(service.priceKobo);
  return {
    ...service,
    id: service.id,
    hospitalId: service.hospitalId,
    serviceName: service.serviceName || "",
    serviceNameLowerCase: lower(service.serviceName),
    serviceCode: service.serviceCode || "",
    serviceCategory: service.serviceCategory || "",
    departmentName: service.departmentName || "",
    departmentId: service.departmentId || "",
    price,
    amount: price,
    type: service.type || "general",
    status: service.status === "INACTIVE" ? "Inactive" : "Active",
  };
};

const toLocalServicePayload = (hospitalId, serviceData = {}) => ({
  hospitalId,
  serviceName: norm(serviceData.serviceName || serviceData.name),
  serviceCode: norm(serviceData.serviceCode || serviceData.code) || serviceCodeFromName(serviceData.serviceName || serviceData.name),
  serviceCategory: norm(serviceData.serviceCategory || serviceData.category) || undefined,
  departmentName: norm(serviceData.departmentName || serviceData.department) || undefined,
  priceKobo: toKobo(serviceData.price ?? serviceData.amount),
});

const toLocalServicePatch = (serviceData = {}) => {
  const patch = {};
  if (serviceData.serviceName !== undefined || serviceData.name !== undefined) {
    patch.serviceName = norm(serviceData.serviceName || serviceData.name);
  }
  if (serviceData.serviceCategory !== undefined || serviceData.category !== undefined) {
    patch.serviceCategory = norm(serviceData.serviceCategory || serviceData.category) || undefined;
  }
  if (serviceData.departmentName !== undefined || serviceData.department !== undefined) {
    patch.departmentName = norm(serviceData.departmentName || serviceData.department) || undefined;
  }
  if (serviceData.price !== undefined || serviceData.amount !== undefined) {
    patch.priceKobo = toKobo(serviceData.price ?? serviceData.amount);
  }
  if (serviceData.status !== undefined) {
    patch.status = lower(serviceData.status) === "inactive" || lower(serviceData.status) === "disabled" ? "INACTIVE" : "ACTIVE";
  }
  return patch;
};

const currentHospitalId = () =>
  sessionStorage.getItem("hospitalId") ||
  localStorage.getItem("hospitalId") ||
  "";

const wrapLocalError = (action, error) => {
  const message = error?.message || String(error);
  if (/bearer|jwt|unauthor/i.test(message)) {
    throw new Error(`${action} failed: Local API token is missing or expired.`);
  }
  if (/hospital|scope|forbidden/i.test(message)) {
    throw new Error(`${action} failed: Hospital scope mismatch.`);
  }
  if (/failed to fetch|network/i.test(message)) {
    throw new Error(`${action} failed: Local API is unavailable.`);
  }
  throw new Error(`${action} failed: ${message}`);
};

const listRaw = async (hospitalId) => {
  const rows = await localApiClient.get(`/services/${encodeURIComponent(hospitalId)}`);
  return (Array.isArray(rows) ? rows : []).map(fromLocalService);
};

const filterServices = (services = [], { searchTerm = "", departmentId = "", departmentName = "", type = "", category = "", names = [] } = {}) => {
  const q = lower(searchTerm);
  const nameSet = Array.isArray(names) ? names.map(lower).filter(Boolean) : [];
  return services.filter((service) => {
    if (service.status === "Inactive") return false;
    if (type && service.type && service.type !== type) return false;
    if (departmentId && service.departmentId && service.departmentId !== departmentId) return false;
    if (departmentName && lower(service.departmentName) !== lower(departmentName)) return false;
    if (category && lower(service.serviceCategory) !== lower(category)) return false;
    if (nameSet.length && !nameSet.includes(service.serviceNameLowerCase)) return false;
    if (q && !service.serviceNameLowerCase.includes(q) && !lower(service.serviceCode).includes(q)) return false;
    return true;
  });
};

export const localServiceService = {
  async addService(hospitalId, serviceData) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const saved = await localApiClient.post("/services", toLocalServicePayload(hospitalId, serviceData));
      const service = fromLocalService(saved);
      return { success: true, id: service.id, data: service };
    } catch (error) {
      wrapLocalError("Create service", error);
    }
  },

  async updateService(serviceId, updatedData = {}) {
    try {
      const hospitalId = updatedData.hospitalId || currentHospitalId();
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const saved = await localApiClient.patch(
        `/services/${encodeURIComponent(hospitalId)}/${encodeURIComponent(serviceId)}`,
        toLocalServicePatch(updatedData)
      );
      return { success: true, data: fromLocalService(saved) };
    } catch (error) {
      wrapLocalError("Update service", error);
    }
  },

  async fetchServices(hospitalId) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      return listRaw(hospitalId);
    } catch (error) {
      wrapLocalError("Fetch services", error);
    }
  },

  async fetchServicesPaginated(hospitalId, pageSize = 20) {
    const services = (await this.fetchServices(hospitalId)).slice(0, pageSize);
    return {
      services,
      lastDoc: null,
      totalServices: services.length,
      hasMore: services.length === pageSize,
    };
  },

  async deleteService(serviceId, hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      await localApiClient.delete(`/services/${encodeURIComponent(hospitalId)}/${encodeURIComponent(serviceId)}`);
      return { success: true };
    } catch (error) {
      wrapLocalError("Disable service", error);
    }
  },

  async fetchServicesByDepartment(departmentId) {
    const hospitalId = currentHospitalId();
    const services = await this.fetchServices(hospitalId);
    return filterServices(services, { departmentId });
  },

  async fetchFilteredServices(hospitalId, departmentId, param) {
    const services = await this.fetchServices(hospitalId);
    return filterServices(services, { departmentId, names: param });
  },

  async fetchFilteredServicesGeneral(hospitalId, param) {
    const services = await this.fetchServices(hospitalId);
    return filterServices(services, { names: param, type: "general" });
  },

  async searchServicesByName(searchTerm, hospitalId, type = "general") {
    const services = await this.fetchServices(hospitalId);
    return filterServices(services, { searchTerm, type });
  },

  async searchServicesByNameBilling(hospitalId, searchTerm) {
    const services = await this.fetchServices(hospitalId);
    return filterServices(services, { searchTerm });
  },

  async searchServicesByCategory(hospitalId, searchTerm, category) {
    const services = await this.fetchServices(hospitalId);
    return filterServices(services, { searchTerm, category });
  },
};

export default localServiceService;
